/**
 * audioPlayer — Audio Lessons playback service (expo-audio engine).
 *
 * A thin wrapper around `expo-audio` that owns playback of the pre-compiled
 * episode MP3 renditions (setup / load / play / pause / seek / progress /
 * cleanup) plus the iOS lock-screen / Now-Playing + Android media-notification
 * controls and background playback. This is the Audio Lessons player and is a
 * SEPARATE subsystem from the chat `audioController` / Azure streaming TTS —
 * they share no store, no service, and no native session ownership. They
 * coordinate ONLY at the OS audio-session level (one output at a time).
 * See docs/specs/mobile/audio-lessons.mobile.md "BOUNDARY" + AUDIO-LESSONS-003/006.
 *
 * Engine note: replaced `react-native-track-player` (v4 broke under RN 0.85's
 * New Architecture; v5 is commercially licensed). `expo-audio` is a first-party
 * Expo module — New-Arch-native by construction — with built-in
 * `MPNowPlayingInfoCenter`/`MPRemoteCommandCenter` + Android MediaSession.
 *
 * Units: expo-audio works in SECONDS; this module's public surface works in
 * MILLISECONDS (matching the transcript timeline + saved progress).
 *
 * Progress: the UI subscribes via `subscribeProgress`/`getProgressSnapshot`
 * (a `useSyncExternalStore` source driven by a JS ticker reading
 * `player.currentTime`). A timer is smoother than expo-audio's status events for
 * the karaoke highlight and survives the player instance being replaced on a
 * teaching-language swap.
 */

import {
  createAudioPlayer,
  setAudioModeAsync,
  setIsAudioActiveAsync,
  type AudioPlayer,
  type AudioMetadata,
} from 'expo-audio';

import { E2E_MODE } from '../config/constants';
import { createLogger } from '../utils/logger';
import { takeAudioFocus, releaseAudioFocus } from './audioFocus';

const log = createLogger('AudioPlayer');

/**
 * Skip native audio in E2E runs (mirrors `streamingTTS.ts`'s `skipNative` — keeps
 * automated runs free of audible playback + native teardown crashes). expo-audio
 * supports web, so web is NOT gated. When disabled the methods are inert no-ops
 * and progress stays at zero.
 */
const PLAYER_ENABLED = !(__DEV__ && E2E_MODE);

/** ±skip step for the optional skip controls + the lock-screen jump buttons. */
export const SKIP_STEP_MS = 10_000;

/** Position/duration poll cadence for the scrubber + karaoke highlight (ms). */
const PROGRESS_TICK_MS = 250;

/** Metadata surfaced on the lock screen / Now-Playing controls. */
export interface AudioPlayerTrack {
  /** Stable id — the episode slug + resolved teaching language. */
  id: string;
  /** Remote MP3 URL of the resolved rendition. */
  url: string;
  title: string;
  /** Series title (shown as the "artist" line). */
  artist?: string;
  artwork?: string;
  /** Known duration in ms (from the rendition) — used until the engine reports one. */
  durationMs?: number;
}

export interface AudioProgressSnapshot {
  positionMs: number;
  durationMs: number;
  playing: boolean;
}

const ZERO: AudioProgressSnapshot = { positionMs: 0, durationMs: 0, playing: false };

let player: AudioPlayer | null = null;
let loadedTrackId: string | null = null;
/** Duration from the rendition metadata — stands in until the engine reports one. */
let renditionDurationMs = 0;
let audioModeReady = false;

// ── Progress store (useSyncExternalStore source) ───────────────────────────────
const listeners = new Set<() => void>();
let snapshot: AudioProgressSnapshot = ZERO;
let ticker: ReturnType<typeof setInterval> | null = null;

function computeSnapshot(): AudioProgressSnapshot {
  if (!player) return ZERO;
  const positionMs = Math.max(0, Math.round((player.currentTime ?? 0) * 1000));
  const engineDurationMs = Math.round((player.duration ?? 0) * 1000);
  const durationMs = engineDurationMs > 0 ? engineDurationMs : renditionDurationMs;
  return { positionMs, durationMs, playing: player.playing ?? false };
}

function publish(next: AudioProgressSnapshot): void {
  if (
    next.positionMs !== snapshot.positionMs ||
    next.durationMs !== snapshot.durationMs ||
    next.playing !== snapshot.playing
  ) {
    snapshot = next;
    for (const l of listeners) l();
  }
}

function startTicker(): void {
  if (ticker || !PLAYER_ENABLED) return;
  ticker = setInterval(() => publish(computeSnapshot()), PROGRESS_TICK_MS);
}

function stopTicker(): void {
  if (ticker) {
    clearInterval(ticker);
    ticker = null;
  }
}

/** Subscribe to progress updates (the UI hook). Starts the ticker while observed. */
export function subscribeProgress(cb: () => void): () => void {
  listeners.add(cb);
  startTicker();
  return () => {
    listeners.delete(cb);
    if (listeners.size === 0) stopTicker();
  };
}

/** Current progress snapshot (stable reference between changes). */
export function getProgressSnapshot(): AudioProgressSnapshot {
  return snapshot;
}

// ── Audio session ───────────────────────────────────────────────────────────────

/**
 * One-time audio-mode setup. `interruptionMode: 'doNotMix'` is REQUIRED for
 * lock-screen controls (exclusive audio focus) and `shouldPlayInBackground`
 * keeps playback alive when backgrounded. This claims the shared session; the
 * chat TTS subsystem must yield/own it separately (see cleanup()).
 */
async function ensureAudioMode(): Promise<void> {
  if (audioModeReady || !PLAYER_ENABLED) return;
  await setAudioModeAsync({
    playsInSilentMode: true,
    shouldPlayInBackground: true,
    interruptionMode: 'doNotMix',
  });
  audioModeReady = true;
}

export async function setup(): Promise<void> {
  await ensureAudioMode();
}

/**
 * Replace the current track with `track`. No-op when the same rendition is
 * already loaded (never tear down a track that may be playing in the
 * background). Claims the lock-screen / Now-Playing slot with the metadata.
 */
export async function load(track: AudioPlayerTrack): Promise<void> {
  if (loadedTrackId === track.id) return;
  renditionDurationMs = track.durationMs ?? 0;

  if (!PLAYER_ENABLED) {
    loadedTrackId = track.id;
    return;
  }

  await ensureAudioMode();

  // Release the previous instance before creating the next.
  if (player) {
    try {
      player.remove();
    } catch (err) {
      log.debug('Failed to release previous player', { err: String(err) });
    }
    player = null;
  }

  player = createAudioPlayer({ uri: track.url }, { updateInterval: PROGRESS_TICK_MS });
  loadedTrackId = track.id;

  const metadata: AudioMetadata = {
    title: track.title,
    artist: track.artist ?? 'Fluencia',
    ...(track.artwork ? { artworkUrl: track.artwork } : {}),
  };
  // Claim the OS Now-Playing slot + start the Android foreground media service.
  player.setActiveForLockScreen(true, metadata);

  publish(computeSnapshot());
  log.debug('Loaded audio track', { id: track.id });
}

/** The id of the currently loaded track (null when nothing is loaded). */
export function getLoadedTrackId(): string | null {
  return loadedTrackId;
}

/** How the lessons player yields when chat TTS takes audio focus: pause. */
function yieldToOtherAudio(): void {
  if (player) {
    player.pause();
    publish(computeSnapshot());
  }
}

export async function play(): Promise<void> {
  if (!player) return;
  // Claim exclusive audio focus — stops chat TTS if it held it (one output at a
  // time; the chat-TTS subsystem yields via its own registered handler).
  takeAudioFocus('audio-lessons', yieldToOtherAudio);
  player.play();
  startTicker();
  publish(computeSnapshot());
}

export async function pause(): Promise<void> {
  if (!player) return;
  player.pause();
  releaseAudioFocus('audio-lessons');
  publish(computeSnapshot());
}

/** Seek to an absolute position in milliseconds (clamped at 0). */
export async function seek(positionMs: number): Promise<void> {
  if (!player) return;
  await player.seekTo(Math.max(0, positionMs) / 1000);
  publish(computeSnapshot());
}

/** Current playback position + duration in milliseconds. */
export async function getProgressMs(): Promise<{ positionMs: number; durationMs: number }> {
  const s = computeSnapshot();
  return { positionMs: s.positionMs, durationMs: s.durationMs };
}

/**
 * Full teardown — stops playback, clears the lock-screen controls, releases the
 * native player, and YIELDS the audio session so the chat TTS can take it. Call
 * ONLY on explicit stop or track replacement, NOT on screen unmount (background
 * playback must survive leaving the screen — AUDIO-LESSONS-003 / 006).
 */
export async function cleanup(): Promise<void> {
  loadedTrackId = null;
  renditionDurationMs = 0;
  releaseAudioFocus('audio-lessons');
  if (player) {
    try {
      player.clearLockScreenControls();
      player.remove();
    } catch (err) {
      log.debug('Failed to release player on cleanup', { err: String(err) });
    }
    player = null;
  }
  if (PLAYER_ENABLED) {
    try {
      await setIsAudioActiveAsync(false);
    } catch (err) {
      log.debug('Failed to deactivate audio session', { err: String(err) });
    }
  }
  publish(ZERO);
}
